import { getEduDemoDataset, getSchoolDemoDataset } from './eduDemoData'
import type { EduDemoDataset } from '../../explore_seeds/edu/eduDemoTypes'
import type { EduBooking } from '../../types/schema'
import type { GateLearner, GateLearnerGuardian } from '../eduHelperJobsService'
import type { PendingFieldPassEntry } from '../eduFieldPassSchoolService'
import type { GetFamilyProgressResult, FamilyProgressMarkItem } from '../eduFamilyService'

export type EduDemoResponder = (payload: unknown) => Promise<unknown>

function responder<T>(fn: (payload: T) => Promise<unknown>): EduDemoResponder {
  return (payload: unknown) => fn(payload as T)
}

async function getDataset(payload?: unknown): Promise<EduDemoDataset> {
  let ws: string | undefined
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if (typeof p.workspaceId === 'string') {
      ws = p.workspaceId
    } else if (typeof p.rootId === 'string') {
      if (p.rootId.startsWith('prog-')) ws = 'sports-academy-demo'
      else if (p.rootId.startsWith('year-jee-neet')) ws = 'coaching-demo'
      else if (p.rootId.startsWith('year-')) ws = 'school-demo'
    } else if (typeof p.code === 'string') {
      if (p.code.startsWith('OLYMPUS-')) ws = 'sports-academy-demo'
      else if (p.code.startsWith('APEX-')) ws = 'coaching-demo'
    }
  }
  const ds = ws ? await getEduDemoDataset(ws) : null
  return ds || (await getSchoolDemoDataset())
}

// Helper to filter items by pagination
function pageItems<T>(items: T[], cursor?: string | null, limit: number = 50): { items: T[]; nextCursor: string | null } {
  const startIndex = cursor ? parseInt(cursor, 10) : 0
  const validStart = isNaN(startIndex) || startIndex < 0 ? 0 : startIndex
  const paged = items.slice(validStart, validStart + limit)
  const next = validStart + limit < items.length ? String(validStart + limit) : null
  return { items: paged, nextCursor: next }
}

export const eduDemoResponders: Record<string, EduDemoResponder> = {
  // -------------------------------------------------------------------------
  // Announcements
  // -------------------------------------------------------------------------
  listEduAnnouncements: responder(async (payload: { workspaceId?: string; nodeIds?: string[]; from?: string; to?: string; limit?: number; cursor?: string }) => {
    const ds = await getDataset(payload)
    let items = ds.announcements
    if (payload?.nodeIds && payload.nodeIds.length > 0) {
      items = items.filter((a) => a.allClasses || a.nodeIds.some((n) => payload.nodeIds!.includes(n)))
    }
    const { items: paged, nextCursor } = pageItems(items, payload?.cursor, payload?.limit ?? 50)
    return { announcements: paged, nextCursor }
  }),

  listFamilyAnnouncements: responder(async (payload: { workspaceId?: string; learnerId?: string; cursor?: string; limit?: number }) => {
    const ds = await getDataset(payload)
    const mapped = ds.announcements.map((a) => ({
      ...a,
      myReactions: [] as string[],
      readByMe: true,
    }))
    const { items: paged, nextCursor } = pageItems(mapped, payload?.cursor, payload?.limit ?? 50)
    return { announcements: paged, nextCursor }
  }),

  // -------------------------------------------------------------------------
  // Attendance Callables
  // -------------------------------------------------------------------------
  getEduSessionRoll: responder(async (payload: { workspaceId?: string; seriesId?: string; date?: string }) => {
    const ds = await getDataset(payload)
    const targetDate = payload?.date || ds.todayIso
    const relevantSeries = ds.sessionSeries.find((s) => s.id === payload?.seriesId) || ds.sessionSeries[0]
    const classId = relevantSeries?.classId || ds.learners[0]?.currentNodeIds[0] || 'class-6a'

    const classLearners = ds.learners
      .filter((l) => l.currentNodeIds.includes(classId))
      .map((l) => ({
        learnerId: l.id,
        displayName: l.displayName,
        rollNo: l.admissionNo || null,
        frozen: false,
      }))

    const dateAttendance = ds.attendance.filter((a) => a.date === targetDate && a.classId === classId)
    const marks: Record<string, string> = {}
    classLearners.forEach((l) => {
      const record = dateAttendance.find((a) => a.learnerId === l.learnerId)
      marks[l.learnerId] = record?.status || 'PRESENT'
    })

    return {
      learners: classLearners,
      marks,
      session: {
        status: 'PLANNED',
        substituteUid: null,
        rollTakenAt: ds.sessions[0]?.rollTakenAt || null,
      },
    }
  }),

  // -------------------------------------------------------------------------
  // Admissions & Enquiries
  // -------------------------------------------------------------------------
  listEduEnquiries: responder(async (payload: { workspaceId?: string; stage?: string | string[]; source?: string; ownerUid?: string }) => {
    const ds = await getDataset(payload)
    let items = ds.enquiries
    if (payload?.stage) {
      const stages = Array.isArray(payload.stage) ? payload.stage : [payload.stage]
      items = items.filter((e) => stages.includes(e.stage))
    }
    if (payload?.source) items = items.filter((e) => e.source === payload.source)
    if (payload?.ownerUid) items = items.filter((e) => e.ownerUid === payload.ownerUid)
    return { enquiries: items }
  }),

  getEduEnquiry: responder(async (payload: { workspaceId?: string; enquiryId: string }) => {
    const ds = await getDataset(payload)
    const enquiry = ds.enquiries.find((e) => e.id === payload?.enquiryId) || ds.enquiries[0]
    return { enquiry }
  }),

  findEduEnquiryDuplicates: async () => {
    return { duplicates: [] }
  },

  getEduEnquiryLink: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { link: ds.enquiryLinks[0] || null }
  }),

  readEduEnquiryForm: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return {
      link: ds.enquiryLinks[0] || null,
      form: {
        title: ds.workspaceId === 'sports-academy-demo'
          ? 'Olympus Sports Academy Admissions 2026'
          : ds.workspaceId === 'coaching-demo'
          ? 'Apex JEE & NEET Admissions 2026'
          : 'Oakridge International School Admissions 2026–27',
        fields: [],
      },
    }
  }),

  getEduAdmissionsSettings: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return ds.admissionsSettings
  }),

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------
  listEduEvents: responder(async (payload: { workspaceId?: string; status?: string; from?: string; to?: string }) => {
    const ds = await getDataset(payload)
    let items = ds.events
    if (payload?.status) items = items.filter((e) => e.status === payload.status)
    return { events: items, nextCursor: null }
  }),

  getEduEvent: responder(async (payload: { workspaceId?: string; eventId: string }) => {
    const ds = await getDataset(payload)
    const event = ds.events.find((e) => e.id === payload?.eventId) || ds.events[0]
    return { event }
  }),

  getEduEventRsvps: responder(async (payload: { workspaceId?: string; eventId: string }) => {
    const ds = await getDataset(payload)
    const rsvps = ds.eventRsvps.filter((r) => r.eventId === payload?.eventId)
    return { rsvps, nextCursor: null }
  }),

  listFamilyEduEvents: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    const events = ds.events.map((e) => ({
      ...e,
      myRsvp: 'GOING',
    }))
    return { events, nextCursor: null }
  }),

  getFamilyEduEvent: responder(async (payload: { workspaceId?: string; eventId: string }) => {
    const ds = await getDataset(payload)
    const event = ds.events.find((e) => e.id === payload?.eventId) || ds.events[0]
    return { event: { ...event, myRsvp: 'GOING' } }
  }),

  // -------------------------------------------------------------------------
  // Family Service Reads
  // -------------------------------------------------------------------------
  listMyFamilyLearners: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    const learners = ds.learners.slice(0, 2).map((l) => ({
      workspaceId: ds.workspaceId,
      workspaceName: ds.workspaceId === 'sports-academy-demo'
        ? 'Olympus Sports Academy'
        : ds.workspaceId === 'coaching-demo'
        ? 'Apex Coaching Centre'
        : 'Oakridge International School',
      learnerId: l.id,
      displayName: l.displayName,
      photoUrl: null,
      relation: 'GUARDIAN' as const,
      frozen: false,
      classNames: [l.currentNodeIds[0] || 'Batch 1'],
      isStaffHere: false,
      trainingProgrammes: [],
    }))
    return { learners }
  }),

  getFamilyToday: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0].id
    const sessions = ds.sessions.slice(0, 3).map((s) => ({
      seriesId: s.seriesId,
      sessionId: s.id!,
      nodeId: s.nodeId,
      classId: s.classId,
      title: 'Session',
      startTime: s.startTime,
      durationMin: s.durationMin,
      status: s.status,
      substituteName: null,
      onlineUrl: null,
      attendanceStatus: 'PRESENT',
    }))
    const homework = ds.homework.slice(0, 2).map((hw) => ({
      checklistId: hw.id,
      title: hw.title,
      instructions: hw.description || '',
      dueDate: hw.eduHomework?.dueDate || ds.todayIso,
      maxScore: hw.eduHomework?.maxScore || null,
      status: 'SUBMITTED',
    }))
    return {
      date: ds.todayIso,
      learnerId: targetLearnerId,
      sessions,
      homework,
    }
  }),

  getFamilyWork: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0].id
    const work = ds.homework.map((hw) => {
      const sub = ds.submissions.find((s) => s.checklistId === hw.id && s.learnerId === targetLearnerId)
      return {
        checklistId: hw.id,
        title: hw.title,
        instructions: hw.description || '',
        dueDate: hw.eduHomework?.dueDate || ds.todayIso,
        maxScore: hw.eduHomework?.maxScore || null,
        status: (sub?.status || 'SUBMITTED') as 'SUBMITTED',
        feedback: sub?.feedback || null,
        score: sub?.score ?? null,
        attachments: [],
        quiz: null,
      }
    })
    return { learnerId: targetLearnerId, work }
  }),

  getFamilyProgress: responder(async (payload: { workspaceId?: string; learnerId?: string; from?: string; to?: string }): Promise<GetFamilyProgressResult> => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0].id

    // Attendance aggregation
    let learnerAtt = ds.attendance.filter((a) => a.learnerId === targetLearnerId)
    if (payload?.from) {
      learnerAtt = learnerAtt.filter((a) => a.date >= payload.from!)
    }
    if (payload?.to) {
      learnerAtt = learnerAtt.filter((a) => a.date <= payload.to!)
    }

    const monthsMap = new Map<
      string,
      { month: string; present: number; late: number; absent: number; excused: number; total: number }
    >()
    const overall = { present: 0, late: 0, absent: 0, excused: 0, total: 0 }

    for (const a of learnerAtt) {
      const dateStr = typeof a.date === 'string' ? a.date : ''
      const monthStr = dateStr.slice(0, 7)
      if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) continue

      if (!monthsMap.has(monthStr)) {
        monthsMap.set(monthStr, {
          month: monthStr,
          present: 0,
          late: 0,
          absent: 0,
          excused: 0,
          total: 0,
        })
      }
      const m = monthsMap.get(monthStr)!
      const status = typeof a.status === 'string' ? a.status.toUpperCase() : ''
      if (status === 'PRESENT') {
        m.present++
        overall.present++
      } else if (status === 'LATE') {
        m.late++
        overall.late++
      } else if (status === 'ABSENT') {
        m.absent++
        overall.absent++
      } else if (status === 'EXCUSED' || status === 'ON_LEAVE') {
        m.excused++
        overall.excused++
      }
      m.total++
      overall.total++
    }

    let months = Array.from(monthsMap.values()).sort((a, b) => a.month.localeCompare(b.month))

    // Fallback if dataset has no attendance for the learner
    if (months.length === 0) {
      const fallbackMonth = (ds.todayIso || '2026-10-21').slice(0, 7)
      months = [
        {
          month: fallbackMonth,
          present: 20,
          late: 1,
          absent: 1,
          excused: 0,
          total: 22,
        },
      ]
      overall.present = 20
      overall.late = 1
      overall.absent = 1
      overall.excused = 0
      overall.total = 22
    }

    // Helper to evaluate lock status
    const isLockedFlag = (val: unknown): boolean | null => {
      if (!val || typeof val !== 'object') return null
      const v = val as Record<string, unknown>
      if (typeof v.isLocked === 'boolean') return v.isLocked
      if (typeof v.status === 'string') return v.status === 'LOCKED'
      return null
    }

    // Marks only for locked terms
    const rawGroups = new Map<
      string,
      {
        subjectNodeId: string
        termId: string
        assessments: {
          id: string
          title: string
          maxMarks: number
          score: number | null
          grade?: string | null
        }[]
      }
    >()

    for (const ass of ds.assessments) {
      const subjectNodeId = ass.nodeId
      const termId = ass.termId
      if (!subjectNodeId || !termId) continue

      const markKey = `${ass.id}_${targetLearnerId}`
      const markDoc = ds.marks[markKey]
      if (!markDoc) continue

      const score = typeof markDoc.score === 'number' ? markDoc.score : null
      const maxMarks = typeof ass.maxMarks === 'number' ? ass.maxMarks : 100

      let grade: string | null = null
      if (score != null && maxMarks > 0 && ds.gradingScheme?.bands) {
        const percent = Math.round((score / maxMarks) * 100)
        const matchedBand = ds.gradingScheme.bands.find((b) => percent >= b.minPercent)
        if (matchedBand) {
          grade = matchedBand.label
        }
      }

      const key = `${subjectNodeId}_${termId}`
      if (!rawGroups.has(key)) {
        rawGroups.set(key, {
          subjectNodeId,
          termId,
          assessments: [],
        })
      }

      rawGroups.get(key)!.assessments.push({
        id: ass.id || '',
        title: ass.title || 'Assessment',
        maxMarks,
        score,
        ...(grade ? { grade } : {}),
      })
    }

    const marksResult: FamilyProgressMarkItem[] = []

    for (const group of rawGroups.values()) {
      if (group.assessments.length === 0) continue
      const { subjectNodeId, termId, assessments } = group

      // Check lock status
      const pairLock = isLockedFlag(ds.markLockStatus[`${subjectNodeId}_${termId}`])
      const termLock = isLockedFlag(ds.markLockStatus[termId])

      let isLocked = false
      if (pairLock === false || termLock === false) {
        isLocked = false
      } else if (pairLock === true || termLock === true) {
        isLocked = true
      } else {
        const assLocks = assessments.map((a) => isLockedFlag(ds.markLockStatus[a.id]))
        if (assLocks.some((l) => l === false)) {
          isLocked = false
        } else if (assLocks.some((l) => l === true)) {
          isLocked = true
        }
      }

      if (!isLocked) continue

      const subjElem = ds.elements?.find((e) => e.id === subjectNodeId)
      const subjectName = subjElem?.title || subjectNodeId

      const term = ds.gradingScheme?.terms?.find((t) => t.id === termId)
      const termName = term?.name || termId

      marksResult.push({
        subjectNodeId,
        subjectName,
        termId,
        termName,
        assessments: assessments.map((a) => ({
          title: a.title,
          maxMarks: a.maxMarks,
          score: a.score,
          ...(a.grade ? { grade: a.grade } : {}),
        })),
      })
    }

    marksResult.sort(
      (a, b) => a.subjectName.localeCompare(b.subjectName) || a.termName.localeCompare(b.termName),
    )

    return {
      attendance: {
        months,
        overall,
      },
      marks: marksResult,
    }
  }),

  // -------------------------------------------------------------------------
  // Fees
  // -------------------------------------------------------------------------
  getEduFeeSettings: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { settings: ds.feeSettings }
  }),

  listEduFeePlans: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { plans: ds.feePlans }
  }),

  getEduFeePlan: responder(async (payload: { workspaceId?: string; planId?: string }) => {
    const ds = await getDataset(payload)
    const plan = ds.feePlans.find((p) => p.id === payload?.planId) || ds.feePlans[0]
    return { plan }
  }),

  listEduDues: responder(async (payload: { workspaceId?: string; nodeId?: string; learnerId?: string; status?: string; overdueOnly?: boolean }) => {
    const ds = await getDataset(payload)
    let items = ds.dues
    if (payload?.nodeId) items = items.filter((d) => d.nodeId === payload.nodeId)
    if (payload?.learnerId) items = items.filter((d) => d.learnerId === payload.learnerId)
    if (payload?.status) items = items.filter((d) => d.status === payload.status)
    if (payload?.overdueOnly) items = items.filter((d) => d.isOverdue)
    return { dues: items }
  }),

  listEduFeePayments: responder(async (payload: { workspaceId?: string; learnerId?: string; nodeId?: string }) => {
    const ds = await getDataset(payload)
    let items = ds.feePayments
    if (payload?.learnerId) items = items.filter((p) => p.learnerId === payload.learnerId)
    if (payload?.nodeId) items = items.filter((p) => p.nodeId === payload.nodeId)
    return { payments: items }
  }),

  getEduFeeReceiptPdfUrl: async () => {
    return { signedUrl: 'https://example.test/receipt.pdf' }
  },

  listFamilyFees: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0].id
    const dues = ds.dues.filter((d) => d.learnerId === targetLearnerId)
    const payments = ds.feePayments.filter((p) => p.learnerId === targetLearnerId)
    return {
      currency: 'INR',
      payOnline: { available: false as const },
      dues: {
        upcoming: dues.filter((d) => d.status === 'DUE' || d.status === 'UPCOMING'),
        overdue: dues.filter((d) => d.status === 'OVERDUE'),
        paid: dues.filter((d) => d.status === 'PAID'),
      },
      payments,
    }
  }),

  getFamilyFeeReceiptPdfUrl: async () => {
    return { signedUrl: 'https://example.test/receipt.pdf' }
  },

  // -------------------------------------------------------------------------
  // Feed Posts
  // -------------------------------------------------------------------------
  listEduFeedPosts: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { posts: ds.feedPosts, nextCursor: null }
  }),

  listFamilyFeedPosts: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    const posts = ds.feedPosts.map((p) => ({
      ...p,
      myReactions: [] as string[],
      seenByMe: true,
    }))
    return { posts, nextCursor: null }
  }),

  listFamilyFeed: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    const posts = ds.feedPosts.map((p) => ({
      ...p,
      myReactions: [] as string[],
      seenByMe: true,
    }))
    return { posts, nextCursor: null }
  }),

  // -------------------------------------------------------------------------
  // Grading & Gradebook
  // -------------------------------------------------------------------------
  getEduGradingScheme: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { scheme: ds.gradingScheme }
  }),

  listEduMarkHistory: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { history: ds.markHistory }
  }),

  getEduMarkLockStatus: responder(async (payload: { workspaceId?: string; assessmentId?: string }) => {
    const ds = await getDataset(payload)
    const id = payload?.assessmentId || ''
    return ds.markLockStatus[id] || { isLocked: false, lockedAt: null, lockedBy: null }
  }),

  getEduGradebook: responder(async (payload: { workspaceId?: string; subjectNodeId?: string }) => {
    const ds = await getDataset(payload)
    const targetNodeId = payload?.subjectNodeId || (ds.elements.find((e) => e.edu?.kind === 'SUBJECT')?.id || 'subj-6a-math')
    const subjectAssessments = ds.assessments.filter((a) => a.nodeId === targetNodeId)

    const learners = ds.learners
      .filter((l) => l.currentNodeIds.includes(ds.learners[0]?.currentNodeIds[0] || 'class-6a'))
      .map((l) => {
        const markCells: Record<string, unknown> = {}
        subjectAssessments.forEach((a) => {
          const m = ds.marks[`${a.id}_${l.id}`]
          markCells[a.id!] = m ? { score: m.score ?? null, state: m.state ?? null, remarks: m.remarks ?? null } : null
        })
        return {
          learnerId: l.id,
          displayName: l.displayName,
          rollNo: l.admissionNo || null,
          marks: markCells,
          terms: {
            'phase-1': {
              percent: 85,
              grade: 'A2',
              missing: 0,
              changedAfterLock: 0,
            },
          },
          year: {
            percent: 85,
            grade: 'A2',
            incompleteTermIds: [],
          },
        }
      })

    return {
      terms: ds.gradingScheme.terms.map((t) => ({
        id: t.id,
        name: t.name,
        weight: t.weight || 50,
        status: 'LOCKED' as const,
      })),
      categories: ds.gradingScheme.categories,
      bands: ds.gradingScheme.bands,
      assessments: subjectAssessments.map((a) => ({
        id: a.id!,
        title: a.title,
        termId: a.termId,
        categoryId: a.categoryId,
        maxMarks: a.maxMarks,
        date: a.date,
        source: a.source,
      })),
      learners,
    }
  }),

  getEduClassGradebook: responder(async (payload: { workspaceId?: string; classId?: string }) => {
    const ds = await getDataset(payload)
    const targetClassId = payload?.classId || ds.learners[0]?.currentNodeIds[0] || 'class-6a'

    const learners = ds.learners
      .filter((l) => l.currentNodeIds.includes(targetClassId))
      .map((l) => ({
        learnerId: l.id,
        displayName: l.displayName,
        rollNo: l.admissionNo || null,
        subjects: {
          'subj-1': {
            terms: {
              'term-1': {
                percent: 85,
                grade: 'A2',
                missing: 0,
                changedAfterLock: 0,
              },
            },
            year: {
              percent: 85,
              grade: 'A2',
              incompleteTermIds: [],
            },
          },
        },
      }))

    return {
      terms: ds.gradingScheme.terms.map((t) => ({
        id: t.id,
        name: t.name,
        weight: t.weight || 50,
        status: 'LOCKED' as const,
      })),
      learners,
    }
  }),

  // -------------------------------------------------------------------------
  // Homework Callables
  // -------------------------------------------------------------------------
  getEduHomeworkRoll: responder(async (payload: { workspaceId?: string; checklistId?: string }) => {
    const ds = await getDataset(payload)
    const targetChecklistId = payload?.checklistId || ds.homework[0]?.id || 'hw-1'
    const hw = ds.homework.find((h) => h.id === targetChecklistId) || ds.homework[0]

    const targetClassId = ds.learners[0]?.currentNodeIds[0] || 'class-6a'
    const learners = ds.learners
      .filter((l) => l.currentNodeIds.includes(targetClassId))
      .map((l) => {
        const sub = ds.submissions.find((s) => s.checklistId === targetChecklistId && s.learnerId === l.id)
        return {
          learnerId: l.id,
          displayName: l.displayName,
          rollNo: l.admissionNo || '',
          status: sub ? sub.status : 'ASSIGNED',
          frozen: false,
          submission: sub,
        }
      })

    return {
      dueDate: hw?.eduHomework?.dueDate || ds.todayIso,
      maxScore: hw?.eduHomework?.maxScore || null,
      learners,
    }
  }),

  listFamilyHomework: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0]?.id
    return {
      homework: ds.homework,
      submissions: ds.submissions.filter((s) => s.learnerId === targetLearnerId),
    }
  }),

  // -------------------------------------------------------------------------
  // Media Consents
  // -------------------------------------------------------------------------
  listEduMediaConsents: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { consents: ds.mediaConsents }
  }),

  getEduMediaConsent: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0]?.id
    const consent = ds.mediaConsents.find((c) => c.learnerId === targetLearnerId)
    return {
      consent: {
        workspaceId: ds.workspaceId,
        learnerId: targetLearnerId,
        level: consent?.level || 'PHOTOS_OK',
      },
    }
  }),

  // -------------------------------------------------------------------------
  // Messaging
  // -------------------------------------------------------------------------
  listEduThreads: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { threads: ds.messageThreads, nextCursor: null, messagingEnabled: true }
  }),

  listEduThreadMessages: responder(async (payload: { workspaceId?: string; threadId?: string }) => {
    const ds = await getDataset(payload)
    const messages = ds.threadMessages.filter((m) => !payload?.threadId || m.threadId === payload.threadId)
    return { messages, nextCursor: null }
  }),

  getEduOfficeHours: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return {
      windows: ds.officeHours,
      isInsideHours: true,
      isInsideOfficeHours: true,
      nextWindowStart: null,
      timezone: 'Asia/Kolkata',
    }
  }),

  listEduMessageableTeachers: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return {
      teachers: ds.staff.map((s) => ({
        uid: s.uid,
        displayName: s.displayName,
        email: s.email,
        title: s.title,
        role: s.role,
      })),
      messagingEnabled: true,
    }
  }),

  // -------------------------------------------------------------------------
  // Portfolio
  // -------------------------------------------------------------------------
  getEduPortfolio: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0]?.id
    const items = ds.portfolioItems.filter((p) => p.learnerId === targetLearnerId)
    return {
      pinnedItems: items.filter((p) => p.pinned),
      items: items.filter((p) => !p.pinned),
      nextCursor: null,
    }
  }),

  getEduPortfolioPdfUrl: async () => {
    return {
      url: 'https://example.test/portfolio.pdf',
      pdfUrl: 'https://example.test/portfolio.pdf',
      storagePath: 'portfolios/test.pdf',
      contentHash: 'hash123',
    }
  },

  // -------------------------------------------------------------------------
  // Quiz
  // -------------------------------------------------------------------------
  getEduQuizSetup: async () => {
    return { quiz: null }
  },

  getEduQuiz: async () => {
    return {
      formId: 'quiz-none',
      fields: [],
      form: { title: 'Demo Quiz' },
      settings: { passPercent: 50, maxAttempts: 3, dueDate: '', maxScore: 10 },
      attemptsUsed: 0,
      attemptsLeft: 3,
      bestResult: null,
      keys: null,
      closed: true,
      review: null,
    }
  },

  // -------------------------------------------------------------------------
  // Report Cards
  // -------------------------------------------------------------------------
  getEduReportCardSettings: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return ds.reportCardSettings
  }),

  listEduReportEntries: responder(async (payload: { workspaceId?: string; classId?: string; termId?: string }) => {
    const ds = await getDataset(payload)
    const classLearners = payload?.classId
      ? ds.learners.filter((l) => l.currentNodeIds.includes(payload.classId!))
      : ds.learners
    const roll = classLearners.map((l, idx) => ({
      learnerId: l.id,
      displayName: l.displayName,
      rollNo: idx + 1,
      subjectIds: [],
    }))
    return { entries: [], roll }
  }),

  listEduReportCards: responder(async (payload: { workspaceId?: string; classId?: string; termId?: string }) => {
    const ds = await getDataset(payload)
    let items = ds.reportCards
    if (payload?.classId) items = items.filter((c) => c.classId === payload.classId)
    if (payload?.termId) items = items.filter((c) => c.termId === payload.termId)
    return { cards: items }
  }),

  getEduReportCard: responder(async (payload: { workspaceId?: string; cardId?: string; learnerId?: string; termId?: string }) => {
    const ds = await getDataset(payload)
    let card = ds.reportCards.find((c) => c.id === payload?.cardId)
    if (!card && payload?.learnerId) {
      card = ds.reportCards.find((c) => c.learnerId === payload.learnerId && (!payload.termId || c.termId === payload.termId))
    }
    return { card: card || ds.reportCards[0] }
  }),

  getEduReportCardPdfUrl: async () => {
    return { signedUrl: 'https://example.test/report-card.pdf' }
  },

  listReportCenterEduCards: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    const cards = ds.reportCards.map((c) => ({
      cardId: c.id!,
      learner: {
        learnerId: c.learnerId,
        displayName: c.snapshot.learner.displayName,
        rollNo: c.snapshot.learner.rollNo || null,
      },
      classId: c.classId,
      termId: c.termId,
      kind: c.kind,
      status: c.status,
      version: c.version,
      pdfStatus: 'READY',
    }))
    return { cards }
  }),

  listFamilyReportCards: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0]?.id
    const cards = ds.reportCards
      .filter((c) => c.learnerId === targetLearnerId)
      .map((c) => ({
        id: c.id!,
        cardId: c.id!,
        termId: c.termId,
        termName: c.snapshot.term.name,
        status: c.status,
        kind: c.kind,
        version: c.version,
      }))
    return { cards }
  }),

  getFamilyReportCard: responder(async (payload: { workspaceId?: string; cardId?: string }) => {
    const ds = await getDataset(payload)
    const card = ds.reportCards.find((c) => c.id === payload?.cardId) || ds.reportCards[0]
    return { card }
  }),

  readSharedEduReportCard: responder(async (payload?: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    return { card: ds.reportCards[0] }
  }),

  // -------------------------------------------------------------------------
  // Leave Callables
  // -------------------------------------------------------------------------
  listFamilyLeave: responder(async (payload: { workspaceId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    const targetLearnerId = payload?.learnerId || ds.learners[0]?.id
    const requests = ds.leaveRequests.filter((lr) => lr.learnerId === targetLearnerId)
    return { requests, nextCursor: null }
  }),

  // -------------------------------------------------------------------------
  // Booking & Entitlements (Training — ED19.a2)
  // -------------------------------------------------------------------------
  getEduBookingSettings: responder(async (payload: { workspaceId?: string; rootId?: string }) => {
    const ds = await getDataset(payload)
    return { settings: ds.bookingSettings }
  }),

  listEduSessionBookings: responder(async (payload: { workspaceId?: string; seriesId: string; date: string; status?: string }) => {
    const ds = await getDataset(payload)
    const key = `${payload.seriesId}_${payload.date}`
    let bookings = ds.sessionBookings[key] || []
    if (payload?.status) bookings = bookings.filter((b) => b.status === payload.status)
    return { bookings }
  }),

  listEduLearnerEntitlements: responder(async (payload: { workspaceId?: string; learnerId: string }) => {
    const ds = await getDataset(payload)
    return { entitlements: ds.learnerEntitlements[payload.learnerId] || [] }
  }),

  listFamilyBookings: responder(async (payload: { workspaceId?: string; learnerId: string; status?: string }) => {
    const ds = await getDataset(payload)
    const bookings: EduBooking[] = []
    for (const sessionList of Object.values(ds.sessionBookings)) {
      for (const b of sessionList) {
        if (b.learnerId === payload.learnerId) {
          if (!payload?.status || b.status === payload.status) {
            bookings.push(b)
          }
        }
      }
    }
    return { bookings }
  }),

  listFamilyEntitlements: responder(async (payload: { workspaceId?: string; learnerId: string }) => {
    const ds = await getDataset(payload)
    return { entitlements: ds.learnerEntitlements[payload.learnerId] || [] }
  }),

  listEduBookingSessions: responder(async (payload: { workspaceId?: string; rootId?: string; seriesId?: string; fromDate?: string; toDate?: string }) => {
    const ds = await getDataset(payload)
    let sessions = ds.bookingSessions
    if (payload?.seriesId) sessions = sessions.filter((s) => s.seriesId === payload.seriesId)
    if (payload?.fromDate) sessions = sessions.filter((s) => s.date >= payload.fromDate!)
    if (payload?.toDate) sessions = sessions.filter((s) => s.date <= payload.toDate!)
    return { sessions }
  }),

  listEduLearnerBookingDetail: responder(async (payload: { workspaceId?: string; learnerId: string; rootId?: string; limit?: number }) => {
    const ds = await getDataset(payload)
    const learnerBookings: EduBooking[] = []
    for (const sessionList of Object.values(ds.sessionBookings)) {
      for (const b of sessionList) {
        if (b.learnerId === payload.learnerId) {
          learnerBookings.push(b)
        }
      }
    }
    const upcomingBookings = learnerBookings.filter((b) => b.date >= ds.todayIso && b.status !== 'CANCELLED')
    const recentBookings = learnerBookings.filter((b) => b.date < ds.todayIso)
    return {
      entitlements: ds.learnerEntitlements[payload.learnerId] || [],
      bookings: learnerBookings,
      upcomingBookings,
      recentBookings,
      strikeCount: 0,
      activeBlock: null,
      strikes: [],
    }
  }),

  listFamilyBookableSessions: responder(async (payload: { workspaceId?: string; learnerId: string; rootId?: string }) => {
    const ds = await getDataset(payload)
    const sessions = ds.bookingSessions.map((bs) => {
      const key = `${bs.seriesId}_${bs.date}`
      const bookingsInSession = ds.sessionBookings[key] || []
      const childBookingMatch = bookingsInSession.find((b) => b.learnerId === payload.learnerId)
      const childBooking = childBookingMatch
        ? {
            id: childBookingMatch.id,
            status: childBookingMatch.status,
            waitlistPosition: childBookingMatch.waitlistPosition,
            lateCancelNow: false,
            freeCancelOnce: true,
          }
        : null
      return {
        seriesId: bs.seriesId,
        date: bs.date,
        startTime: bs.startTime,
        durationMin: 90,
        nodeId: bs.nodeId,
        title: 'Training Session',
        seatsLeft: Math.max(0, bs.capacity - bs.bookedCount),
        childBooking,
        dropInAvailable: true,
        dropInPriceMinor: 150000,
      }
    })
    return { sessions }
  }),

  // -------------------------------------------------------------------------
  // Progress & Certification (Training — ED19.a2)
  // -------------------------------------------------------------------------
  listEduProgressMetrics: responder(async (payload: { workspaceId?: string; rootId?: string; includeArchived?: boolean }) => {
    const ds = await getDataset(payload)
    let metrics = ds.progressMetrics
    if (payload?.rootId) metrics = metrics.filter((m) => m.rootId === payload.rootId)
    if (!payload?.includeArchived) metrics = metrics.filter((m) => !m.archived)
    return { metrics }
  }),

  listEduPendingSelfReports: responder(async (payload: { workspaceId?: string; rootId: string }) => {
    const ds = await getDataset(payload)
    return { items: ds.pendingSelfReports[payload.rootId] || [], nextCursor: null }
  }),

  getEduProgressLevels: responder(async (payload: { workspaceId?: string; rootId: string }) => {
    const ds = await getDataset(payload)
    const doc = ds.progressLevels[payload.rootId] || { version: 1, levels: [] }
    return { version: doc.version, levels: doc.levels }
  }),

  getEduLearnerProgress: responder(async (payload: { workspaceId?: string; rootId: string; learnerId: string }) => {
    const ds = await getDataset(payload)
    const key = `${payload.rootId}_${payload.learnerId}`
    return ds.learnerProgress[key] || { metrics: [], currentLevel: null, history: [], nextLevel: null }
  }),

  listEduProgramProgress: responder(async (payload: { workspaceId?: string; rootId: string }) => {
    const ds = await getDataset(payload)
    const learners = ds.programProgress[payload.rootId] || []
    return { learners, totalPendingSelfReports: (ds.pendingSelfReports[payload.rootId] || []).length }
  }),

  listEduCertTemplates: responder(async (payload: { workspaceId?: string; includeArchived?: boolean }) => {
    const ds = await getDataset(payload)
    let templates = ds.certTemplates
    if (!payload?.includeArchived) templates = templates.filter((t) => !t.archived)
    return { templates }
  }),

  listEduCertificates: responder(async (payload: { workspaceId?: string; rootId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    let certificates = ds.certificates
    if (payload?.rootId) certificates = certificates.filter((c) => c.rootId === payload.rootId)
    if (payload?.learnerId) certificates = certificates.filter((c) => c.learnerId === payload.learnerId)
    return { certificates }
  }),

  getEduCertificatePdfUrl: responder(async (payload: { workspaceId?: string; certificateId: string }) => {
    return { url: `https://mock.storage.test/certificates/${payload.certificateId}.pdf` }
  }),

  listEduPracticeLogs: responder(async (payload: { workspaceId?: string; rootId?: string; learnerId?: string }) => {
    const ds = await getDataset(payload)
    let logs = ds.practiceLogs
    if (payload?.rootId) logs = logs.filter((l) => l.rootId === payload.rootId)
    if (payload?.learnerId) logs = logs.filter((l) => l.learnerId === payload.learnerId)
    return { logs, nextCursor: null }
  }),

  verifyEduCertificate: responder(async (payload: { code: string; workspaceId?: string }) => {
    if (payload?.workspaceId !== 'sports-academy-demo') {
      const err = new Error('NOT_IN_DEMO') as Error & { code: string }
      err.code = 'failed-precondition'
      throw err
    }
    const cleanCode = (payload.code || '').trim().toUpperCase()
    const mod = await import('../../explore_seeds/edu/sportsAcademyDemo')
    const ds = mod.buildSportsAcademyDemo()
    const found = ds.certificates.find((c) => c.code.toUpperCase() === cleanCode)
    if (found) {
      return {
        title: found.snapshot.rendered.title,
        holder: found.snapshot.holder.displayName,
        programme: found.snapshot.programme.title,
        issueDate: found.issueDate,
        expiryDate: found.expiryDate,
        status: 'VALID',
        workspaceName: found.snapshot.workspace.name,
        demoNotice: 'Demo certificate: not a real award',
      }
    }
    const err = new Error('CERTIFICATE_NOT_FOUND') as Error & { code: string }
    err.code = 'not-found'
    throw err
  }),

  // -------------------------------------------------------------------------
  // Parent-Teacher Meetings (PTM — ED16 / ED19.a2)
  // -------------------------------------------------------------------------
  listEduPtmDays: responder(async (payload: { workspaceId?: string; startDate?: string; endDate?: string; module?: 'teaching' | 'training' }) => {
    const ds = await getDataset(payload)
    let days = ds.ptmDays
    if (payload?.startDate) days = days.filter((d) => d.date >= payload.startDate!)
    if (payload?.endDate) days = days.filter((d) => d.date <= payload.endDate!)
    if (payload?.module) days = days.filter((d) => d.module === payload.module)
    return { days }
  }),

  getEduPtmDay: responder(async (payload: { workspaceId?: string; dayId: string }) => {
    const ds = await getDataset(payload)
    const day = ds.ptmDays.find((d) => d.id === payload.dayId) || ds.ptmDays[0]
    return { day }
  }),

  getEduPtmTeacherWindows: responder(async (payload: { workspaceId?: string; dayId: string; teacherUid: string }) => {
    const ds = await getDataset(payload)
    const windows = ds.ptmTeacherWindows.find((w) => w.dayId === payload.dayId && w.teacherUid === payload.teacherUid) || null
    return { windows }
  }),

  listEduPtmSlots: responder(async (payload: { workspaceId?: string; dayId?: string; teacherUid?: string }) => {
    const ds = await getDataset(payload)
    return { slots: ds.ptmSlots }
  }),

  listFamilyEduPtmDays: responder(async (payload: { workspaceId?: string; learnerId: string }) => {
    const ds = await getDataset(payload)
    return { days: ds.ptmDays.filter((d) => d.status === 'ACTIVE') }
  }),

  listFamilyEduPtmTeachers: responder(async (payload: { workspaceId?: string; learnerId: string; dayId: string }) => {
    const ds = await getDataset(payload)
    const teachers = ds.ptmTeacherWindows
      .filter((w) => w.dayId === payload.dayId)
      .map((w) => {
        const booking = ds.ptmBookings.find((b) => b.dayId === payload.dayId && b.teacherUid === w.teacherUid && b.learnerId === payload.learnerId) || null
        return {
          teacherUid: w.teacherUid,
          teacherName: w.teacherName || 'Teacher',
          slotDurationMin: w.slotDurationMin,
          roomOrOnline: w.roomOrOnlineUrl || 'Room 101',
          isOnline: false,
          booking,
        }
      })
    return { teachers }
  }),

  listFamilyEduPtmSlots: responder(async (payload: { workspaceId?: string; learnerId: string; dayId: string; teacherUid: string }) => {
    const ds = await getDataset(payload)
    const existingBooking = ds.ptmBookings.find((b) => b.dayId === payload.dayId && b.teacherUid === payload.teacherUid && b.learnerId === payload.learnerId) || null
    return {
      slots: ds.ptmSlots,
      hasBookingWithTeacher: Boolean(existingBooking),
      existingBooking,
    }
  }),

  getEduPtmPrivateNotes: responder(async (payload: { workspaceId?: string; bookingId: string }) => {
    const ds = await getDataset(payload)
    const notes = ds.ptmPrivateNotes[payload.bookingId] || 'Private meeting observation: student is attentive and shows strong curiosity.'
    return { notes, updatedAt: `${ds.todayIso}T10:00:00Z` }
  }),

  listFamilyEduPtmSummaries: responder(async (payload: { workspaceId?: string; learnerId: string }) => {
    const ds = await getDataset(payload)
    const summaries = ds.ptmSummaries.filter((s) => s.learnerId === payload.learnerId)
    return { summaries }
  }),

  // -------------------------------------------------------------------------
  // Helper Jobs & School Field Pass (EM2.a / EM3.a / EM2a3a.fix2)
  // -------------------------------------------------------------------------
  getMyEduJobs: responder(async (payload: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    const session = ds.sessionSeries[0]
    const sessionId = session?.id || 'series-6a-math'
    const sessionElem = session?.nodeId ? ds.elements.find((e) => e.id === session.nodeId) : undefined
    const sessionName = sessionElem?.title || 'Grade 6A Math'
    const routeId = 'route-campus-bus-1'
    const routeName = 'Campus Shuttle Route 1'

    return {
      eduJobs: ['ROLL', 'GATE', 'BUS', 'DESK'] as const,
      eduRouteIds: [routeId],
      eduRollSessionIds: [sessionId],
      routes: [{ id: routeId, name: routeName }],
      sessions: [{ id: sessionId, name: sessionName }],
    }
  }),

  getHelperEduJobs: responder(async (payload: { workspaceId?: string; helperUid?: string }) => {
    const ds = await getDataset(payload)
    const session = ds.sessionSeries[0]
    const sessionId = session?.id || 'series-6a-math'
    const routeId = 'route-campus-bus-1'

    return {
      eduJobs: ['ROLL', 'BUS'],
      eduRouteIds: [routeId],
      eduRollSessionIds: [sessionId],
    }
  }),

  listEduTransportRoutes: responder(async (payload: { workspaceId?: string; includeArchived?: boolean }) => {
    const ds = await getDataset(payload)
    const routeId = 'route-campus-bus-1'
    const routeName = 'Campus Shuttle Route 1'
    const learnerIds = ds.learners.slice(0, 5).map((l) => l.id)

    const routes = [
      {
        id: routeId,
        workspaceId: payload?.workspaceId || 'school-demo',
        name: routeName,
        code: 'BUS-01',
        stops: [
          { id: 'stop-1', name: 'Main Gate', order: 1, learnerIds: learnerIds.slice(0, 2) },
          { id: 'stop-2', name: 'Metro Station', order: 2, learnerIds: learnerIds.slice(2, 5) },
        ],
        helperUids: ['demo-helper-1'],
        archived: false,
      },
    ]

    return { routes }
  }),

  listGateLearners: responder(async (payload: { workspaceId?: string }) => {
    const ds = await getDataset(payload)
    // 2-4 demo learners per EM2a3a.fix2 / EM-P5 with guardian name + relation only, never phone or email
    const chosenLearners = ds.learners.slice(0, 3)

    const learners: GateLearner[] = chosenLearners.map((l) => {
      const classId = l.currentNodeIds[0] || ''
      const classElem = classId ? ds.elements.find((e) => e.id === classId) : undefined
      const classOrBatch = classElem?.title || classId || 'Class 6A'

      const guardians: GateLearnerGuardian[] = (l.guardianUids || []).map((uid, idx) => {
        let displayName = 'Guardian'
        if (uid.includes('meera.sharma')) displayName = 'Meera Sharma'
        else if (uid.includes('rohit.verma')) displayName = 'Rohit Verma'
        else if (uid.startsWith('guardian-')) {
          const raw = uid.replace('guardian-', '').replace(/[._]/g, ' ')
          displayName = raw.replace(/\b\w/g, (c) => c.toUpperCase())
        }
        const relation = idx === 0 ? 'Mother' : idx === 1 ? 'Father' : 'Guardian'
        return {
          uid,
          displayName,
          relation,
        }
      })

      return {
        learnerId: l.id,
        displayName: l.displayName,
        classOrBatch,
        guardians,
      }
    })

    return { learners }
  }),

  listPendingFieldPassEntries: responder(async (payload: { workspaceId?: string; sessionId?: string; status?: string }) => {
    const ds = await getDataset(payload)
    const session = ds.sessionSeries[0]
    const sessionId = session?.id || 'series-6a-math'

    if (payload?.sessionId && payload.sessionId !== sessionId) {
      return { entries: [] }
    }

    const rawStatus = typeof payload?.status === 'string' && payload.status.trim()
      ? payload.status.trim().toUpperCase()
      : 'PENDING'

    if (rawStatus !== 'PENDING') {
      return { entries: [] }
    }

    const demoLearners = ds.learners.slice(0, 3)
    const entry: PendingFieldPassEntry = {
      id: 'field-pass-entry-demo-1',
      workspaceId: ds.workspaceId,
      passId: 'pass-visiting-coach-1',
      type: 'ROLL',
      eduScope: {
        kind: 'SESSION',
        sessionId,
      },
      entries: demoLearners.map((l, idx) => ({
        learnerId: l.id,
        status: idx === 1 ? 'LATE' : 'PRESENT',
      })),
      source: 'FIELD_PASS',
      status: 'PENDING',
      submittedAt: `${ds.todayIso}T09:30:00Z`,
      submittedBy: 'visiting-coach-demo',
      displayName: 'Visiting Coach',
    }

    return { entries: [entry] }
  }),
}
